import React, { useState, useEffect, useRef, useCallback } from 'react'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Building2,
  Loader2,
  Save,
  CheckCircle2,
  AlertCircle,
  Utensils,
  BookOpen,
  Dumbbell,
  ClipboardList,
  Heart,
  ChefHat,
  Palette,
  ImageIcon,
  FileText,
  Link2,
  Check,
  Upload,
  Trash2,
  ShieldAlert,
  Plus,
  User,
  Mail,
  Lock,
} from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

// ─── Constants ────────────────────────────────────────────────────────────────

const TEMPLATES = [
  { key: 'food_templates',              label: 'Food Templates',              icon: Utensils,     color: 'orange' },
  { key: 'meal_templates',              label: 'Meal Templates',              icon: BookOpen,     color: 'amber'  },
  { key: 'exercise_templates',          label: 'Exercise Templates',          icon: Dumbbell,     color: 'blue'   },
  { key: 'exercise_plan_templates',     label: 'Exercise Plan Templates',     icon: ClipboardList, color: 'indigo' },
  { key: 'nutrition_program_templates', label: 'Nutrition Program Templates', icon: Heart,        color: 'green'  },
  { key: 'recipes_templates',           label: 'Recipes Templates',           icon: ChefHat,      color: 'pink'   },
]

// Maps our internal template key → the API's `*_purchased` boolean field
const TEMPLATE_PURCHASED_KEY = {
  food_templates:              'food_templates_purchased',
  meal_templates:              'meal_templates_purchased',
  exercise_templates:          'exercise_templates_purchased',
  exercise_plan_templates:     'exercise_plan_templates_purchased',
  nutrition_program_templates: 'nutrition_program_templates_purchased',
  recipes_templates:           'recipes_purchased',
}

// Maps our internal template key → the copy-templates endpoint (mirrors Angular's typesOfTemplates)
const TEMPLATE_COPY_ENDPOINT = {
  food_templates:              'copyandcreatefoodtemplates',
  meal_templates:              'copyandcreatemealtemplates',
  exercise_templates:          'copyandcreateexercisetemplates',
  exercise_plan_templates:     'copyandcreateexerciseplantemplates',
  nutrition_program_templates: 'copyandcreatenutritionprograms',
  recipes_templates:           'copyandcreaterecipetemplates',
}

const TEMPLATE_PALETTE = {
  orange: {
    idle:    'border-slate-200 bg-white hover:border-orange-200 hover:bg-orange-50/50',
    active:  'border-orange-300 bg-orange-50 ring-2 ring-orange-100',
    icon:    'text-orange-500 bg-orange-100',
    check:   'bg-orange-500',
  },
  amber: {
    idle:    'border-slate-200 bg-white hover:border-amber-200 hover:bg-amber-50/50',
    active:  'border-amber-300 bg-amber-50 ring-2 ring-amber-100',
    icon:    'text-amber-500 bg-amber-100',
    check:   'bg-amber-500',
  },
  blue: {
    idle:    'border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/50',
    active:  'border-blue-300 bg-blue-50 ring-2 ring-blue-100',
    icon:    'text-blue-500 bg-blue-100',
    check:   'bg-blue-500',
  },
  indigo: {
    idle:    'border-slate-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/50',
    active:  'border-indigo-300 bg-indigo-50 ring-2 ring-indigo-100',
    icon:    'text-indigo-500 bg-indigo-100',
    check:   'bg-indigo-500',
  },
  green: {
    idle:    'border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/50',
    active:  'border-emerald-300 bg-emerald-50 ring-2 ring-emerald-100',
    icon:    'text-emerald-500 bg-emerald-100',
    check:   'bg-emerald-500',
  },
  pink: {
    idle:    'border-slate-200 bg-white hover:border-pink-200 hover:bg-pink-50/50',
    active:  'border-pink-300 bg-pink-50 ring-2 ring-pink-100',
    icon:    'text-pink-500 bg-pink-100',
    check:   'bg-pink-500',
  },
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionStatus({ status }) {
  if (!status) return null
  if (status === 'success') {
    return (
      <span className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium animate-fade-in">
        <CheckCircle2 className="h-4 w-4" />
        Saved successfully
      </span>
    )
  }
  return (
    <span className="flex items-center gap-1.5 text-sm text-red-500 font-medium animate-fade-in">
      <AlertCircle className="h-4 w-4" />
      {typeof status === 'string' && status !== 'error' ? status : 'Failed to save'}
    </span>
  )
}

function TemplateCard({ template, selected, onToggle }) {
  const palette = TEMPLATE_PALETTE[template.color]
  const Icon = template.icon
  return (
    <button
      type="button"
      onClick={() => onToggle(template.key)}
      className={`relative w-full text-left rounded-2xl border p-4 transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-400 ${
        selected ? palette.active : palette.idle
      }`}
    >
      {/* Check badge */}
      <div
        className={`absolute top-3 right-3 h-5 w-5 rounded-full flex items-center justify-center transition-all duration-200 ${
          selected ? `${palette.check} shadow-sm` : 'border-2 border-slate-200 bg-white'
        }`}
      >
        {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      </div>

      <div className={`inline-flex p-2.5 rounded-xl mb-3 ${palette.icon}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-sm font-semibold text-slate-800 pr-6 leading-snug">{template.label}</p>
    </button>
  )
}

function CircularProgress({ percent, size = 120, strokeWidth = 10 }) {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (Math.min(percent, 100) / 100) * circumference
  return (
    <svg width={size} height={size} className="progress-ring">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E2E8F0" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        className="stroke-fitness-primary"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 300ms ease' }}
      />
    </svg>
  )
}

function TemplateCopyOverlay({ percent }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40">
      <div className="relative flex items-center justify-center">
        <CircularProgress percent={percent} />
        <span className="absolute text-lg font-semibold text-white">{Math.round(percent)}%</span>
      </div>
    </div>
  )
}

function ColorSwatch({ label, value, onChange, description }) {
  const inputRef = useRef(null)
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      {description && <p className="text-xs text-slate-500">{description}</p>}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="relative h-10 w-10 rounded-xl border-2 border-white shadow-md hover:scale-105 transition-transform cursor-pointer flex-shrink-0 overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          style={{ backgroundColor: value }}
          aria-label={`Pick ${label}`}
        >
          <input
            ref={inputRef}
            type="color"
            value={value}
            onChange={e => onChange(e.target.value)}
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
          />
        </button>
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 flex-1">
          <span className="text-xs font-mono text-slate-600 tracking-wider uppercase">{value}</span>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors px-3 py-2 rounded-xl hover:bg-slate-100"
        >
          Change
        </button>
      </div>
    </div>
  )
}

function ImageUploadZone({ label, description, icon: Icon, value, file, onChange, onClear }) {
  const inputRef = useRef(null)
  const [dragging, setDragging] = useState(false)

  const handleDrop = useCallback(e => {
    e.preventDefault()
    setDragging(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped && dropped.type.startsWith('image/')) onChange(dropped)
  }, [onChange])

  const handleDragOver = e => { e.preventDefault(); setDragging(true) }
  const handleDragLeave = () => setDragging(false)
  const handleFileChange = e => { if (e.target.files[0]) onChange(e.target.files[0]) }

  const preview = file ? URL.createObjectURL(file) : (value || null)

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      {description && <p className="text-xs text-slate-500 mb-2">{description}</p>}

      {preview ? (
        <div className="relative group rounded-2xl overflow-hidden border-2 border-slate-200 bg-slate-50">
          <img
            src={preview}
            alt={label}
            className="w-full h-36 object-contain p-3"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="px-3 py-1.5 bg-white text-slate-800 text-xs font-medium rounded-lg hover:bg-slate-100 transition-colors"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={onClear}
              className="px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => inputRef.current?.click()}
          className={`relative rounded-2xl border-2 border-dashed transition-all duration-200 cursor-pointer p-6 flex flex-col items-center justify-center gap-2 min-h-[120px] ${
            dragging
              ? 'border-fitness-primary-light bg-fitness-primary/5'
              : 'border-slate-200 bg-slate-50/50 hover:border-slate-300 hover:bg-slate-50'
          }`}
        >
          <div className="p-2.5 rounded-xl bg-slate-100 text-slate-500">
            <Icon className="h-5 w-5" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-600">
              {dragging ? 'Drop to upload' : 'Click or drag to upload'}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">PNG, JPG, SVG up to 5MB</p>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  )
}

function SectionSaveBar({ onSave, saving, status, label = 'Save Changes' }) {
  return (
    <div className="flex items-center justify-between pt-5 border-t border-slate-100 mt-2">
      <SectionStatus status={status} />
      <Button
        onClick={onSave}
        disabled={saving}
        className="rounded-xl gap-2 bg-fitness-primary hover:bg-fitness-primary/90 text-primary-foreground ml-auto"
        size="sm"
      >
        {saving ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
        ) : (
          <><Save className="h-4 w-4" /> {label}</>
        )}
      </Button>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const CompanySettings = () => {
  const { companyUrl, user } = useAuthStore()
  const isSuperuser = user?.role?.id === 1 || user?.role?.name === 'Superuser'

  // ── Company selector ─────────────────────────────────────────────────────
  const [companies, setCompanies] = useState([])
  const [selectedCompanyId, setSelectedCompanyId] = useState(null)
  const [loadingCompanies, setLoadingCompanies] = useState(true)
  const [companiesError, setCompaniesError] = useState(null)

  // ── Section 1: Company Info ──────────────────────────────────────────────
  const [companyName, setCompanyName] = useState('')
  const [companyUrlSlug, setCompanyUrlSlug] = useState('')
  const [infoSaving, setInfoSaving] = useState(false)
  const [infoStatus, setInfoStatus] = useState(null)

  // ── Section 2: Templates ─────────────────────────────────────────────────
  const [selectedTemplates, setSelectedTemplates] = useState([])
  const [templatesSaving, setTemplatesSaving] = useState(false)
  const [templatesStatus, setTemplatesStatus] = useState(null)
  const [templatesProgress, setTemplatesProgress] = useState(0)

  // ── Section 3: Themes ────────────────────────────────────────────────────
  const [primaryColor, setPrimaryColor] = useState('#2563EB')
  const [secondaryColor, setSecondaryColor] = useState('#0EA5E9')
  const [logoFile, setLogoFile] = useState(null)
  const [logoUrl, setLogoUrl] = useState(null)
  const [dashboardFile, setDashboardFile] = useState(null)
  const [dashboardUrl, setDashboardUrl] = useState(null)
  const [pdfHeaderFile, setPdfHeaderFile] = useState(null)
  const [pdfHeaderUrl, setPdfHeaderUrl] = useState(null)
  const [themesSaving, setThemesSaving] = useState(false)
  const [themesStatus, setThemesStatus] = useState(null)

  // ── Add company dialog ───────────────────────────────────────────────────
  const [showAddCompanyDialog, setShowAddCompanyDialog] = useState(false)
  const [newCompanyName, setNewCompanyName] = useState('')
  const [newFirstName, setNewFirstName] = useState('')
  const [newLastName, setNewLastName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newCompanyDomain, setNewCompanyDomain] = useState('')
  const [addCompanySaving, setAddCompanySaving] = useState(false)
  const [addCompanyError, setAddCompanyError] = useState(null)

  // ── Fetch companies ──────────────────────────────────────────────────────
  const fetchCompanies = useCallback(async ({ selectId = null } = {}) => {
    if (!companyUrl) return
    setLoadingCompanies(true)
    setCompaniesError(null)
    const result = await apiService.get(
      apiService.withCompanyEndpoint('/companyadmin/getcompanies', companyUrl)
    )
    if (result.success && Array.isArray(result.data)) {
      setCompanies(result.data)
      if (selectId) {
        setSelectedCompanyId(selectId)
      } else {
        const isSuper = user?.role?.id === 1 || user?.role?.name === 'Superuser'
        // Company Admin: pre-select their own company; Super Admin: first in list
        const defaultId = !isSuper && user?.company_id
          ? (result.data.find(c => c.id === user.company_id)?.id ?? result.data[0]?.id)
          : result.data[0]?.id
        if (defaultId) setSelectedCompanyId(defaultId)
      }
    } else {
      setCompaniesError(result.error || 'Failed to load companies.')
    }
    setLoadingCompanies(false)
  }, [companyUrl, user])

  useEffect(() => {
    fetchCompanies()
  }, [fetchCompanies])

  // ── Populate all sections from company object when selection changes ──────
  useEffect(() => {
    if (!selectedCompanyId) return
    const company = companies.find(c => c.id === selectedCompanyId)
    if (!company) return

    // Section 1 — Company Info
    setCompanyName(company.name || '')
    setCompanyUrlSlug(company.company_url || '')

    // Section 2 — Templates: derive selected keys from *_purchased booleans
    const active = TEMPLATES
      .filter(t => company[TEMPLATE_PURCHASED_KEY[t.key]])
      .map(t => t.key)
    setSelectedTemplates(active)

    // Section 3 — Themes
    setPrimaryColor(company.color_code || '#2563EB')
    setSecondaryColor(company.secondary_color || '#0EA5E9')
    setLogoUrl(company.logo || null)
    setDashboardUrl(company.dashboard_image || null)
    setPdfHeaderUrl(company.pdf_image || null)
    // Clear any previously staged files when switching companies
    setLogoFile(null)
    setDashboardFile(null)
    setPdfHeaderFile(null)
  }, [selectedCompanyId, companies])

  // ── Status auto-clear ────────────────────────────────────────────────────
  const withAutoReset = (setter) => (value) => {
    setter(value)
    if (value) setTimeout(() => setter(null), 4000)
  }

  const setInfoStatusAuto = withAutoReset(setInfoStatus)
  const setTemplatesStatusAuto = withAutoReset(setTemplatesStatus)
  const setThemesStatusAuto = withAutoReset(setThemesStatus)

  // ── Save: Company Info ───────────────────────────────────────────────────
  const handleSaveInfo = async () => {
    setInfoSaving(true)
    const result = await apiService.post(
      apiService.withCompanyEndpoint(`/companyadmin/updatecompany/${selectedCompanyId}`, companyUrl),
      { name: companyName, company_url: companyUrlSlug }
    )
    setInfoStatusAuto(result.success ? 'success' : (result.error || 'error'))
    setInfoSaving(false)
  }

  // ── Save: Templates ──────────────────────────────────────────────────────
  const handleToggleTemplate = (key) => {
    setSelectedTemplates(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    )
  }

  const handleSaveTemplates = async () => {
    const company = companies.find(c => c.id === selectedCompanyId)
    // Only newly-selected templates need to be copied — already-purchased ones are copied already
    const templatesToCopy = selectedTemplates.filter(key => !company?.[TEMPLATE_PURCHASED_KEY[key]])

    if (templatesToCopy.length === 0) {
      setTemplatesStatusAuto('success')
      return
    }

    setTemplatesSaving(true)
    setTemplatesProgress(0)

    const increment = 100 / templatesToCopy.length
    let hadError = false

    await Promise.all(templatesToCopy.map(async (key) => {
      const result = await apiService.post(
        apiService.withCompanyEndpoint(`/superadmin/${TEMPLATE_COPY_ENDPOINT[key]}`, companyUrl),
        { company_id: selectedCompanyId }
      )
      if (!result.success) hadError = true
      setTemplatesProgress(prev => Math.min(prev + increment, 100))
    }))

    setTemplatesSaving(false)
    setTemplatesStatusAuto(hadError ? 'error' : 'success')
    if (!hadError) {
      fetchCompanies({ selectId: selectedCompanyId })
    }
  }

  // ── Save: Themes ─────────────────────────────────────────────────────────
  const handleSaveThemes = async () => {
    setThemesSaving(true)
    const formData = new FormData()
    formData.append('color_code', primaryColor)
    formData.append('secondary_color', secondaryColor)
    if (logoFile) formData.append('logo', logoFile)
    if (dashboardFile) formData.append('dashboard_image', dashboardFile)
    if (pdfHeaderFile) formData.append('pdf_image', pdfHeaderFile)

    const result = await apiService.put(
      apiService.withCompanyEndpoint(`/companyadmin/updatecompany/${selectedCompanyId}`, companyUrl),
      formData
    )
    setThemesStatusAuto(result.success ? 'success' : (result.error || 'error'))
    setThemesSaving(false)
  }

  // ── Delete company ───────────────────────────────────────────────────────
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteSaving, setDeleteSaving] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const isProtectedCompany = selectedCompanyId === 1

  const handleOpenDeleteDialog = () => {
    setDeleteError(null)
    setShowDeleteDialog(true)
  }

  const handleDeleteCompany = async () => {
    if (isProtectedCompany || !selectedCompanyId) return

    setDeleteSaving(true)
    setDeleteError(null)

    const result = await apiService.deleteCompany(companyUrl, selectedCompanyId)

    if (!result.success) {
      setDeleteError(result.error || 'Failed to delete company.')
      setDeleteSaving(false)
      return
    }

    setDeleteSaving(false)
    setShowDeleteDialog(false)
    setSelectedCompanyId(null)
    await fetchCompanies()
  }

  const hasCompanySelected = !!selectedCompanyId

  // ── Add company ───────────────────────────────────────────────────────
  const resetAddCompanyForm = () => {
    setNewCompanyName('')
    setNewFirstName('')
    setNewLastName('')
    setNewEmail('')
    setNewPassword('')
    setNewCompanyDomain('')
    setAddCompanyError(null)
  }

  const handleOpenAddCompanyDialog = () => {
    resetAddCompanyForm()
    setShowAddCompanyDialog(true)
  }

  const handleAddCompany = async () => {
    if (!newCompanyName.trim() || !newFirstName.trim() || !newLastName.trim() ||
        !newEmail.trim() || !newPassword.trim() || !newCompanyDomain.trim()) {
      setAddCompanyError('Please fill in all fields.')
      return
    }

    setAddCompanySaving(true)
    setAddCompanyError(null)

    const companyResult = await apiService.createCompany(companyUrl, {
      name: newCompanyName.trim(),
      company_url: newCompanyDomain.trim(),
    })

    if (!companyResult.success) {
      setAddCompanyError(companyResult.error || 'Failed to create company.')
      setAddCompanySaving(false)
      return
    }

    const createdCompany = companyResult.data?.success
    const newCompanyId = createdCompany?.id

    const adminResult = await apiService.createCompanyAdmin(companyUrl, {
      name: newCompanyName.trim(),
      firstname: newFirstName.trim(),
      lastname: newLastName.trim(),
      email: newEmail.trim(),
      password: newPassword,
      company_url: newCompanyDomain.trim(),
      company_id: newCompanyId,
    })

    if (!adminResult.success) {
      setAddCompanyError(
        `Company "${newCompanyName.trim()}" was created, but the company admin could not be created: ${adminResult.error || 'Unknown error'}`
      )
      setAddCompanySaving(false)
      await fetchCompanies({ selectId: newCompanyId })
      return
    }

    setAddCompanySaving(false)
    setShowAddCompanyDialog(false)
    resetAddCompanyForm()
    await fetchCompanies({ selectId: newCompanyId })
  }

  return (
    <div className="space-y-6">
      {templatesSaving && <TemplateCopyOverlay percent={templatesProgress} />}

      {/* ── Header ── */}
      <PageHeader
        backTo="/platform-setup"
        backLabel="Platform Setup"
        title="Company Settings"
        subtitle="Manage company profile, templates and theme configuration"
        actions={(
          <>
            {isSuperuser && (
              <Button
                onClick={handleOpenAddCompanyDialog}
                size="sm"
                className="gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Add Company
              </Button>
            )}
            <div className="hidden items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 md:flex">
              <Building2 className="h-3.5 w-3.5 text-slate-500" />
              <span className="text-xs font-medium text-slate-600">
                {isSuperuser ? 'Super Admin' : 'Company Admin'}
              </span>
            </div>
          </>
        )}
      />

      {/* ── Company Selector (Super Admin) / Company Display (Company Admin) ── */}
      <Card className="shadow-sm border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-2.5 bg-fitness-primary/5 rounded-xl flex-shrink-0">
              <Building2 className="h-5 w-5 text-fitness-primary" />
            </div>
            <div className="flex-1">
              {isSuperuser ? (
                <>
                  <p className="text-sm font-semibold text-slate-800 mb-0.5">Select Company</p>
                  <p className="text-xs text-slate-500 mb-3">Settings are applied per company</p>
                  {loadingCompanies ? (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Loading companies…</span>
                    </div>
                  ) : companiesError ? (
                    <div className="flex items-center gap-2 text-red-500">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">{companiesError}</span>
                    </div>
                  ) : (
                    <Select
                      value={selectedCompanyId ? String(selectedCompanyId) : ''}
                      onValueChange={val => setSelectedCompanyId(Number(val))}
                    >
                      <SelectTrigger className="max-w-sm rounded-xl">
                        <SelectValue placeholder="Choose a company…" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mb-1">Your Company</p>
                  {loadingCompanies ? (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Loading…</span>
                    </div>
                  ) : companiesError ? (
                    <div className="flex items-center gap-2 text-red-500">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">{companiesError}</span>
                    </div>
                  ) : (
                    <p className="text-base font-semibold text-slate-900">
                      {companies.find(c => c.id === selectedCompanyId)?.name || '—'}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Sections (only when a company is selected) ── */}
      {hasCompanySelected && (
        <div className="space-y-6">

          {/* ── Section 1: Company Information ── */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader className="px-6 pt-6 pb-0">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-xl">
                    <Building2 className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold text-slate-800">Company Information</CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">Update the company's display name and URL slug</p>
                  </div>
                </div>

                {isSuperuser && (
                  isProtectedCompany ? (
                    <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                      <ShieldAlert className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>Protected — cannot delete</span>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleOpenDeleteDialog}
                      className="rounded-xl gap-1.5 text-red-500 border-red-200 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Company
                    </Button>
                  )
                )}
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Company Name */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700">
                    Company Name
                  </label>
                  <Input
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    placeholder="Acme Fitness Co."
                    className="rounded-xl"
                  />
                  <p className="text-xs text-slate-400">The display name shown throughout the platform</p>
                </div>

                {/* Company URL */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700">
                    Company URL Slug
                  </label>
                  <div className="relative">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={companyUrlSlug}
                      onChange={e => setCompanyUrlSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="acme-fitness"
                      className="pl-9 rounded-xl font-mono text-sm"
                    />
                  </div>
                  <p className="text-xs text-slate-400">Used in API endpoints — lowercase letters, numbers, hyphens only</p>
                </div>
              </div>

              <SectionSaveBar
                onSave={handleSaveInfo}
                saving={infoSaving}
                status={infoStatus}
                label="Save Company Info"
              />
            </CardContent>
          </Card>

          {/* ── Section 2: Template Assignment (Super Admin only) ── */}
          {isSuperuser && (
            <Card className="shadow-sm border-slate-200">
              <CardHeader className="px-6 pt-6 pb-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-xl">
                    <ClipboardList className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold text-slate-800">Template Assignment</CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Select which templates are available to this company&nbsp;
                      <span className="font-medium text-slate-600">({selectedTemplates.length} selected)</span>
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {TEMPLATES.map(template => (
                    <TemplateCard
                      key={template.key}
                      template={template}
                      selected={selectedTemplates.includes(template.key)}
                      onToggle={handleToggleTemplate}
                    />
                  ))}
                </div>

                <SectionSaveBar
                  onSave={handleSaveTemplates}
                  saving={templatesSaving}
                  status={templatesStatus}
                  label="Save Templates"
                />
              </CardContent>
            </Card>
          )}

          {/* ── Section 3: Themes ── */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader className="px-6 pt-6 pb-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-xl">
                  <Palette className="h-4 w-4 text-slate-600" />
                </div>
                <div>
                  <CardTitle className="text-base font-semibold text-slate-800">Theme Configuration</CardTitle>
                  <p className="text-xs text-slate-500 mt-0.5">Brand colors and imagery for this company</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-8">

              {/* Color pickers */}
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-4">Brand Colors</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-5 bg-slate-50 rounded-2xl border border-slate-100">
                  <ColorSwatch
                    label="Primary Color"
                    description="Main action color — buttons, links, highlights"
                    value={primaryColor}
                    onChange={setPrimaryColor}
                  />
                  <ColorSwatch
                    label="Secondary Color"
                    description="Supporting color — gradients, accents, badges"
                    value={secondaryColor}
                    onChange={setSecondaryColor}
                  />
                </div>

                {/* Live preview swatch */}
                <div className="mt-3 flex items-center gap-3">
                  <div className="text-xs text-slate-500 font-medium">Preview:</div>
                  <div
                    className="h-8 rounded-xl flex-1 max-w-xs shadow-sm"
                    style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
                  />
                  <span className="text-xs text-slate-400">Brand gradient</span>
                </div>
              </div>

              {/* Image uploads */}
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-4">Brand Assets</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <ImageUploadZone
                    label="Company Logo"
                    description="Shown in navigation and emails"
                    icon={Upload}
                    value={logoUrl}
                    file={logoFile}
                    onChange={file => { setLogoFile(file); setLogoUrl(null) }}
                    onClear={() => { setLogoFile(null); setLogoUrl(null) }}
                  />
                  <ImageUploadZone
                    label="Dashboard Image"
                    description="Hero image on the dashboard header"
                    icon={ImageIcon}
                    value={dashboardUrl}
                    file={dashboardFile}
                    onChange={file => { setDashboardFile(file); setDashboardUrl(null) }}
                    onClear={() => { setDashboardFile(null); setDashboardUrl(null) }}
                  />
                  <ImageUploadZone
                    label="PDF Header Image"
                    description="Header image on generated PDF reports"
                    icon={FileText}
                    value={pdfHeaderUrl}
                    file={pdfHeaderFile}
                    onChange={file => { setPdfHeaderFile(file); setPdfHeaderUrl(null) }}
                    onClear={() => { setPdfHeaderFile(null); setPdfHeaderUrl(null) }}
                  />
                </div>
              </div>

              <SectionSaveBar
                onSave={handleSaveThemes}
                saving={themesSaving}
                status={themesStatus}
                label="Save Theme"
              />
            </CardContent>
          </Card>

        </div>
      )}

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { if (!deleteSaving) setShowDeleteDialog(open) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2.5 bg-red-100 rounded-xl">
                <Trash2 className="h-5 w-5 text-red-500" />
              </div>
              <DialogTitle className="text-lg font-semibold text-slate-800">
                Delete Company
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-slate-500 leading-relaxed pt-1">
              You are about to permanently delete{' '}
              <span className="font-semibold text-slate-700">{companyName || 'this company'}</span>.
              This action cannot be undone and will remove all associated data, users, and settings.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>All company data will be permanently erased. This cannot be recovered.</span>
          </div>

          {deleteError && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{deleteError}</span>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              variant="outline"
              className="rounded-xl flex-1 sm:flex-none"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCompany}
              disabled={deleteSaving}
              className="rounded-xl flex-1 sm:flex-none bg-red-500 hover:bg-red-600 text-white gap-1.5"
            >
              {deleteSaving ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Deleting…</>
              ) : (
                <><Trash2 className="h-4 w-4" /> Delete Company</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Company Dialog ── */}
      <Dialog open={showAddCompanyDialog} onOpenChange={(open) => { if (!addCompanySaving) setShowAddCompanyDialog(open) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2.5 bg-fitness-primary/10 rounded-xl">
                <Building2 className="h-5 w-5 text-fitness-primary" />
              </div>
              <DialogTitle className="text-lg font-semibold text-slate-800">
                Add Company
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-slate-500 leading-relaxed pt-1">
              Create a new company and its first company admin user.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Company Name</label>
              <Input
                value={newCompanyName}
                onChange={e => setNewCompanyName(e.target.value)}
                placeholder="Acme Fitness Co."
                className="rounded-xl"
                disabled={addCompanySaving}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">First Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={newFirstName}
                    onChange={e => setNewFirstName(e.target.value)}
                    placeholder="John"
                    className="pl-9 rounded-xl"
                    disabled={addCompanySaving}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">Last Name</label>
                <Input
                  value={newLastName}
                  onChange={e => setNewLastName(e.target.value)}
                  placeholder="Doe"
                  className="rounded-xl"
                  disabled={addCompanySaving}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="john@acme.com"
                  className="pl-9 rounded-xl"
                  disabled={addCompanySaving}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 rounded-xl"
                  disabled={addCompanySaving}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Company Domain</label>
              <div className="relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={newCompanyDomain}
                  onChange={e => setNewCompanyDomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="acme-fitness"
                  className="pl-9 rounded-xl font-mono text-sm"
                  disabled={addCompanySaving}
                />
              </div>
              <p className="text-xs text-slate-400">Used in API endpoints — lowercase letters, numbers, hyphens only</p>
            </div>

            {addCompanyError && (
              <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{addCompanyError}</span>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              variant="outline"
              className="rounded-xl flex-1 sm:flex-none"
              onClick={() => setShowAddCompanyDialog(false)}
              disabled={addCompanySaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddCompany}
              disabled={addCompanySaving}
              className="rounded-xl flex-1 sm:flex-none bg-fitness-primary hover:bg-fitness-primary/90 text-primary-foreground gap-1.5"
            >
              {addCompanySaving ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</>
              ) : (
                <><Plus className="h-4 w-4" /> Create Company</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default CompanySettings
